import { useState } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import type { FieldMode } from '../hooks/useCaseField';
import {
  PayerSection,
  InsuranceSection,
  ClinicalSection,
  ProceduresSection,
  SurgerySection,
  PreopSection,
  GuarantorSection,
  DocumentsSection,
  NotesSection,
  StatusSection,
  GapAnalysisSection,
} from './sections';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`case-tabpanel-${index}`}
    aria-labelledby={`case-tab-${index}`}
  >
    {value === index && (
      <Box sx={{ pt: 3, pb: 1.5, px: 1 }}>
        {children}
      </Box>
    )}
  </div>
);

interface CaseFormTabsProps {
  mode: FieldMode;
  errors?: { [key: string]: string | undefined };
  company?: string;
  centerId?: string;
}

const CaseFormTabs: React.FC<CaseFormTabsProps> = ({ mode, errors = {}, company = '', centerId = '' }) => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="case form tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: '40px',
            '& .MuiTab-root': {
              minHeight: '40px',
              padding: '6px 16px',
              fontSize: '0.875rem',
            },
          }}
        >
          <Tab label="Assessment" />
          <Tab label="Insurance" />
          <Tab label="Procedures" />
          <Tab label="Surgery" />
          <Tab label="Preop" />
          <Tab label="Guarantor" />
          <Tab label="Documents" />
          <Tab label="Notes" />
          <Tab label="Status" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <GapAnalysisSection />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <PayerSection mode={mode} company={company} centerId={centerId} errors={errors} />
        <InsuranceSection mode={mode} />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <ClinicalSection mode={mode} errors={errors} />
        <ProceduresSection mode={mode} />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <SurgerySection mode={mode} />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <PreopSection mode={mode} />
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <GuarantorSection mode={mode} />
      </TabPanel>

      <TabPanel value={tabValue} index={6}>
        <DocumentsSection mode={mode} />
      </TabPanel>

      <TabPanel value={tabValue} index={7}>
        <NotesSection mode={mode} />
      </TabPanel>

      <TabPanel value={tabValue} index={8}>
        <StatusSection mode={mode} />
      </TabPanel>
    </Box>
  );
};

export default CaseFormTabs;
